import type {
    BrandInterface,
    ColorInterface,
    FillInterface,
    GradientInterface,
    PaletteInterface,
    SpecialGradientInterface,
    SurfaceInterface,
    TextInterface,
} from '../types';

const Text: TextInterface = {
    x10: 'rgba(0, 0, 0, 0.9)',
    x20: 'rgba(0, 0, 0, 0.55)',
    x30: 'rgba(0, 0, 0, 0.3)',
    Black: 'rgba(0, 0, 0, 0.9)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(255, 255, 255, 1)',
};

const Fill: FillInterface = {
    x10: 'rgba(0, 0, 0, 0.9)',
    x20: 'rgba(0, 0, 0, 0.35)',
    x30: 'rgba(0, 0, 0, 0.2)',
    x40: 'rgba(0, 0, 0, 0.1)',
    x50: 'rgba(0, 0, 0, 0.07)',
    x55: 'rgba(0, 0, 0, 0.05)',
    x60: 'rgba(0, 0, 0, 0.03)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(255, 255, 255, 1)',
};

const Surface: SurfaceInterface = {
    x10: 'rgba(255, 255, 255, 1)',
    x20: 'rgba(255, 255, 255, 1)',
    x30: 'rgba(255, 255, 255, 1)',
    x40: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(26, 26, 26, 1)',
    Overlay: 'rgba(0, 0, 0, 0.5)',
    Foreground: 'rgba(0, 0, 0, 0.05)',
};

const Sell: ColorInterface = {
    x10: 'rgba(0, 0, 0, 0.9)',
    x20: 'rgba(0, 0, 0, 0.05)',
    x30: 'rgba(0, 0, 0, 0.1)',
    x40: 'rgba(0, 0, 0, 0.45)',
    x50: 'rgba(0, 0, 0, 0.6)',
};

const Purple: ColorInterface = {
    x10: 'rgba(91, 63, 230, 1)',
    x20: 'rgba(91, 63, 230, 0.12)',
    x30: 'rgba(91, 63, 230, 0.16)',
    x40: 'rgba(76, 53, 193, 1)',
    x50: 'rgba(55, 38, 138, 1)',
};

const Blue: ColorInterface = {
    x10: 'rgba(10, 70, 224, 1)',
    x20: 'rgba(10, 70, 224, 0.12)',
    x30: 'rgba(10, 70, 224, 0.16)',
    x40: 'rgba(8, 59, 188, 1)',
    x50: 'rgba(6, 42, 134, 1)',
};

const Teal: ColorInterface = {
    x10: 'rgba(0, 181, 217, 1)',
    x20: 'rgba(0, 181, 217, 0.08)',
    x30: 'rgba(0, 181, 217, 0.12)',
    x40: 'rgba(0, 152, 182, 1)',
    x50: 'rgba(0, 109, 130, 1)',
};

const Green: ColorInterface = {
    x10: 'rgba(0, 182, 122, 1)',
    x20: 'rgba(0, 182, 122, 0.14)',
    x30: 'rgba(0, 182, 122, 0.2)',
    x40: 'rgba(0, 153, 102, 1)',
    x50: 'rgba(0, 109, 73, 1)',
};

const Yellow: ColorInterface = {
    x10: 'rgba(255, 214, 0, 1)',
    x20: 'rgba(255, 214, 0, 0.08)',
    x30: 'rgba(255, 214, 0, 0.12)',
    x40: 'rgba(214, 180, 0, 1)',
    x50: 'rgba(153, 128, 0, 1)',
};
const Orange: ColorInterface = {
    x10: 'rgba(242, 101, 0, 1)',
    x20: 'rgba(242, 101, 0, 0.12)',
    x30: 'rgba(242, 101, 0, 0.16)',
    x40: 'rgba(203, 85, 0, 1)',
    x50: 'rgba(145, 61, 0, 1)',
};

const Red: ColorInterface = {
    x10: 'rgba(224, 42, 50, 1)',
    x20: 'rgba(224, 42, 50, 0.12)',
    x30: 'rgba(224, 42, 50, 0.16)',
    x40: 'rgba(224, 42, 50, 1)',
    x50: 'rgba(134, 25, 30, 1)',
};

const Critical: ColorInterface = Red;

const Yes: ColorInterface = Green;
const No: ColorInterface = Red;

const Gradient: GradientInterface = {
    x10: 'radial-gradient(100% 100% at 0% 100%, #00B5D9 0%, #0A46E0 31.77%, #5B3FE6 66.15%, #D90048 100%)',
};

const Special: SpecialGradientInterface = {
    Mint: '#00D991',
    Black: 'linear-gradient(135deg, #B2ADA1 0%, #A09C91 25%, #D1CEC7 50%, #8E8A81 75%, #B2ADA1 100%)',
    Gold: '#E9B600',
    Silver: '#B8CBD9',
    Bronze: '#E5AA95',
    Platinum: 'linear-gradient(135deg, #D9D9D9 0%, #AEAEAE 25%, #E8E8E8 50%, #828282 75%, #D9D9D9 100%)',
    Diamond:
        'linear-gradient(135deg, #95E5E5 0%, #A1A1E5 11.11%, #E5ACE5 22.22%, #F2F2DA 44.44%, #ACE5E5 55.56%, #7478A6 66.67%, #A1A1E5 77.78%, #D9D9C3 88.89%, #95E595 100%)',
    Brand: 'rgba(40, 204, 149, 1)',
};

const Brand: BrandInterface = {
    Primary: 'rgba(40, 204, 149, 1)',
    Secondary: 'rgba(0, 50, 33, 1)',
    Tertiary: '#003221',

    // Deprecated
    x20: 'rgba(229, 251, 244, 1)',
    x30: 'rgba(179, 244, 222, 1)',
    x40: 'rgba(128, 236, 200, 1)',
    x50: 'rgba(77, 228, 178, 1)',
    x60: 'rgba(0, 195, 130, 1)',
    x70: 'rgba(0, 152, 102, 1)',
    x80: 'rgba(0, 109, 73, 1)',
};

/** @deprecated Use `Palette` from `@/DesignSystem3/Palette` instead. */
export const Palette_LightMode: PaletteInterface = {
    Text,
    Fill,
    Surface,
    Yes,
    No,
    Sell,
    Critical,
    Purple,
    Blue,
    Teal,
    Green,
    Yellow,
    Orange,
    Red,
    Gradient,
    Special,
    Brand,
};
